import { Close, Info, Save } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Fade,
  Grid,
  MenuItem,
  TextField,
  Theme,
  Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { deepEqual } from 'fast-equals';
import { Form, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { FC, Fragment, useState, useEffect } from 'react';
import * as Yup from 'yup';
// Components
import { Card, Loader, Modal, ImageModal, SMBSelect } from '../../../components';
import {
  createChecklistTemplate,
  updateChecklistTemplate,
  getCatalogs,
  getChecklistTypes,
  getTaskTypes,
  getTasks,
  createTask,
  getChecklistTemplatesWorkItemCategories,
  getChecklistTemplatesRequirementTypes,
  getChecklistTemplates,
  createChecklistTemplatesWorkItemCategory
} from '../../../fetch';
import {
  IChecklistTemplateDetail,
  IChecklistType,
  IChecklistTemplateWorkItemCategory,
  IChecklistTemplateRequirementType,
  IChecklistTemplate,
  ICatalog,
  ITaskType,
  ISageTask
} from '../../../models';
import { ChecklistTemplatesWorkItems } from './ChecklistTemplatesWorkItems';
import { ChecklistTemplatesMaterialsProvided } from './ChecklistTemplatesMaterialsProvided';

interface IChecklistTemplateModal {
  open: boolean;
  onClose: () => void;
  currentChecklistTemplate: IChecklistTemplateDetail | null;
  fetchChecklistTemplates: () => void;
}

const ChecklistTemplateSchema = Yup.object().shape({
  name: Yup.string().max(255, 'Max 255 characters'),
  description: Yup.string().max(200, 'Max 200 characters'),
  sageTaskId: Yup.number().typeError('Must be a number').required('Required'),
  typeId: Yup.string().required('Required'),
  predecessorChecklistTemplateId: Yup.string(),
  workItems: Yup.array().of(
    Yup.object().shape({
      workItemId: Yup.number().nullable(),
      otherCategory: Yup.string().nullable(),
      description: Yup.string().max(500, 'Max 500 characters'),
      photoRequirementTypeId: Yup.number().nullable(),
      noteRequirementTypeId: Yup.number().nullable(),
      catalogId: Yup.number().nullable(),
      categoryId: Yup.mixed().nullable()
    })
  ),
  materialsProvided: Yup.array().of(
    Yup.object().shape({
      materialsProvidedId: Yup.number().nullable(),
      description: Yup.string().max(200, 'Max 200 characters'),
      manufacturer: Yup.string().max(100, 'Max 100 characters').nullable(),
      model: Yup.string().max(100, 'Max 100 characters').nullable()
    })
  )
});

export const ChecklistTemplateModal: FC<IChecklistTemplateModal> = ({
  open,
  onClose,
  currentChecklistTemplate,
  fetchChecklistTemplates
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [currentWorkItemIndex, setCurrentWorkItemIndex] = useState<number | null>(null);
  const [currentMaterialIndex, setCurrentMaterialIndex] = useState<number | null>(null);
  const [modalImageSrc, setModalImageSrc] = useState<string | null>(null);
  const [isLoadingTypes, setLoadingTypes] = useState<boolean>(false);
  const [isLoadingTaskTypes, setLoadingTaskTypes] = useState<boolean>(false);
  const [isLoadingTasks, setLoadingTasks] = useState<boolean>(false);
  const [types, setTypes] = useState<IChecklistType[]>([]);
  const [taskTypes, setTaskTypes] = useState<ITaskType[]>([]);
  const [isLoadingCategories, setLoadingCategories] = useState<boolean>(false);
  const [workItemCategories, setWorkItemCategories] = useState<
    IChecklistTemplateWorkItemCategory[]
  >([]);
  const [requirementTypes, setRequirementTypes] = useState<IChecklistTemplateRequirementType[]>([]);
  const [checklistTemplates, setChecklistTemplates] = useState<IChecklistTemplate[]>([]);
  const [isLoadingTemplates, setLoadingTemplates] = useState<boolean>(false);
  const [catalogs, setCatalogs] = useState<ICatalog[]>([]);
  const [isLoadingCatalogs, setLoadingCatalogs] = useState<boolean>(false);
  const [selectedCatalogImage, setSelectedCatalogImage] = useState<string>('');
  const [sageTaskIdOptions, setSageTaskIdOptions] = useState<readonly ISageTask[]>([]);

  const fetchCatalogs = async () => {
    setLoadingCatalogs(true);
    try {
      const res = await getCatalogs();
      setCatalogs(res.records);
    } catch (error) {
    } finally {
      setLoadingCatalogs(false);
    }
  };

  const fetchAllChecklistTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await getChecklistTemplates();
      setChecklistTemplates(res.records);
    } catch (error) {
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchChecklistTypes = async () => {
    setLoadingTypes(true);
    try {
      const res = await getChecklistTypes();
      setTypes(res.records);
    } catch (error) {
    } finally {
      setLoadingTypes(false);
    }
  };

  const fetchTaskTypes = async () => {
    setLoadingTaskTypes(true);
    try {
      const res = await getTaskTypes();
      setTaskTypes(res.records);
    } catch (error) {
    } finally {
      setLoadingTaskTypes(false);
    }
  };

  const fetchChecklistWorkItemCategories = async () => {
    setLoadingCategories(true);
    try {
      const res = await getChecklistTemplatesWorkItemCategories();
      setWorkItemCategories(res.records);
    } catch (error) {
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchChecklistRequirementTypes = async () => {
    const res = await getChecklistTemplatesRequirementTypes();
    setRequirementTypes(res.records);
  };

  const fetchTasks = async () => {
    setLoadingTasks(true);
    try {
      const res = await getTasks();
      setSageTaskIdOptions(res.records);
    } catch (error) {
    } finally {
      setLoadingTasks(false);
    }
  };

  useEffect(() => {
    fetchChecklistTypes();
    fetchChecklistWorkItemCategories();
    fetchChecklistRequirementTypes();
    fetchAllChecklistTemplates();
    fetchCatalogs();
    fetchTaskTypes();
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setModalImageSrc(null);
    setCurrentWorkItemIndex(null);
    setCurrentMaterialIndex(null);
    onClose();
  };

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          name: currentChecklistTemplate?.name ?? '',
          description: currentChecklistTemplate?.description ?? '',
          sageTaskId: currentChecklistTemplate?.sageTaskId ?? '',
          typeId: currentChecklistTemplate?.typeId ?? '',
          predecessorChecklistTemplateId:
            currentChecklistTemplate?.predecessorChecklistTemplateId ?? '',
          workItems: currentChecklistTemplate?.workItems ?? [],
          materialsProvided: currentChecklistTemplate?.materialsProvided ?? [],
          taskTypeNumber:
            sageTaskIdOptions.find(option => option.recnum === currentChecklistTemplate?.sageTaskId)
              ?.taskTypeId ?? ''
        }}
        validationSchema={ChecklistTemplateSchema}
        onSubmit={async (values, actions) => {
          try {
            const workItems = await Promise.all(
              // don't need otherCatagory field in the data
              values.workItems.map(async ({ otherCategory, ...item }) => {
                let newCat = item.categoryId;
                // if they select "other" category then create the new category upon save
                if (item.categoryId && item.categoryId === 'other') {
                  newCat = await createChecklistTemplatesWorkItemCategory({
                    name: otherCategory
                  });
                }
                return {
                  ...item,
                  catalogId: item.catalogId ? Number(item.catalogId) : null,
                  categoryId: item.categoryId ? Number(newCat) : null,
                  photoRequirementTypeId: item.photoRequirementTypeId
                    ? Number(item.photoRequirementTypeId)
                    : null,
                  noteRequirementTypeId: item.noteRequirementTypeId
                    ? Number(item.noteRequirementTypeId)
                    : null
                };
              })
            );
            const data = {
              ...values,
              sageTaskId: Number(values.sageTaskId),
              typeId: Number(values.typeId),
              predecessorChecklistTemplateId: values.predecessorChecklistTemplateId
                ? Number(values.predecessorChecklistTemplateId)
                : null,
              workItems
            };

            const isExistingSageId = Boolean(
              sageTaskIdOptions.find(sageTask => sageTask.recnum === Number(values.sageTaskId))
            );
            if (!isExistingSageId) {
              try {
                const sageResponse = await createTask({
                  recnum: Number(values.sageTaskId),
                  taskName: values.name,
                  taskType: Number(values.typeId)
                });
                if (sageResponse.Detail) {
                  enqueueSnackbar(sageResponse.Detail, {
                    variant: 'error'
                  });
                } else {
                  enqueueSnackbar('New Sage record saved!', {
                    variant: 'success'
                  });
                }
                await fetchTasks();
              } catch (err) {
                enqueueSnackbar('Error saving to Sage!', {
                  variant: 'error'
                });
              }
            }

            const res = currentChecklistTemplate
              ? await updateChecklistTemplate(currentChecklistTemplate.checklistTemplateId, data)
              : await createChecklistTemplate(data);

            if (res && res.Detail) {
              enqueueSnackbar(`${res.Detail}`, {
                variant: 'error'
              });
            } else if (
              res &&
              res.Errors &&
              Object.values(res.Errors)[0] &&
              Object.values(Object.values(res.Errors)[0])[0]
            ) {
              enqueueSnackbar(`${Object.values(Object.values(res.Errors)[0])[0]}`, {
                variant: 'error'
              });
            } else {
              enqueueSnackbar(
                `Checklist Template ${currentChecklistTemplate ? 'updated' : 'created'}!`,
                {
                  variant: 'success'
                }
              );
              onClose();
              fetchChecklistTemplates();
              fetchChecklistWorkItemCategories();
              actions.resetForm();
            }
          } catch (error) {
            enqueueSnackbar(`Error, checklist template update failed, please try again.`, {
              variant: 'error'
            });
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          validateField,
          errors,
          touched,
          handleSubmit,
          dirty,
          isValid,
          handleBlur
        }) => {
          const hasDuplicateCategory = workItemCategories
            .map(cat => cat.name)
            .includes(values.workItems[currentWorkItemIndex]?.otherCategory);
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    resetForm();
                    handleClose();
                  } else {
                    return;
                  }
                } else {
                  handleClose();
                }
              }}
              maxWidth="lg"
            >
              {isSubmitting && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <Box marginBottom="1rem" className={classes.title}>
                    <Typography variant="h5">
                      {currentChecklistTemplate
                        ? `Edit Checklist Template`
                        : 'Add Checklist Template'}
                    </Typography>
                  </Box>
                  <Card icon={<Info />} title="Info">
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          autoComplete="nope"
                          label="Name"
                          name="name"
                          value={values.name}
                          onBlur={handleBlur}
                          size="small"
                          onChange={e => setFieldValue('name', e.target.value)}
                          error={Boolean(touched.name && errors.name)}
                          helperText={touched.name && errors.name}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          autoComplete="nope"
                          multiline
                          minRows={2}
                          label="Description"
                          name="description"
                          value={values.description}
                          onBlur={handleBlur}
                          size="small"
                          onChange={e => setFieldValue('description', e.target.value)}
                          error={Boolean(touched.description && errors.description)}
                          helperText={touched.description && errors.description}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3} alignSelf="flex-start">
                        <Autocomplete
                          fullWidth
                          freeSolo
                          openOnFocus
                          autoSelect
                          size="small"
                          id="sageTaskId"
                          disabled={isLoadingTasks}
                          inputValue={values.sageTaskId.toString()}
                          value={values.sageTaskId.toString()}
                          onChange={(event: React.SyntheticEvent, value: ISageTask): void => {
                            if (!value) {
                              setFieldValue('sageTaskId', '');
                              return;
                            }
                            setFieldValue('sageTaskId', value.recnum);
                            setTimeout(() => {
                              validateField('sageTaskId');
                            }, 100);

                            // Set the task type to the task type of the chosen Sage ID
                            const taskType = taskTypes.find(
                              taskType => taskType.taskTypeNumber === value.taskTypeId
                            )?.taskTypeNumber;
                            if (taskType) {
                              setFieldValue('taskTypeNumber', taskType);
                            }
                          }}
                          onInputChange={(event: React.SyntheticEvent, value): void => {
                            if (!value) {
                              setFieldValue('sageTaskId', '');
                              return;
                            }
                            setFieldValue('sageTaskId', value);
                            setTimeout(() => {
                              validateField('sageTaskId');
                            }, 100);

                            // Set the task type to the task type of the chosen Sage ID
                            const sageTaskTypeNumber = sageTaskIdOptions.find(
                              sageTaskOption => sageTaskOption.recnum === Number(value)
                            )?.taskTypeId;
                            const taskType = taskTypes.find(
                              taskType => taskType.taskTypeNumber === sageTaskTypeNumber
                            )?.taskTypeNumber;
                            if (taskType) {
                              setFieldValue('taskTypeNumber', taskType);
                            }
                          }}
                          getOptionLabel={(option: ISageTask) => {
                            if (typeof option === 'string') return option;
                            return option?.recnum?.toString();
                          }}
                          renderOption={(props, task: ISageTask) => {
                            return (
                              <Box
                                component="li"
                                {...props}
                                key={`${task.recnum}`}
                                value={task.recnum}
                              >
                                {task.taskName} - {task.recnum}
                              </Box>
                            );
                          }}
                          renderInput={params => (
                            <TextField
                              size="small"
                              {...params}
                              error={Boolean(errors?.sageTaskId)}
                              helperText={errors.sageTaskId}
                              label="Sage Task ID"
                            />
                          )}
                          options={sageTaskIdOptions}
                          filterOptions={(options, params) => {
                            const { inputValue } = params;
                            const inputLowerCase = inputValue.toLowerCase();
                            const filtered = options.filter(
                              option =>
                                option.taskName.toLowerCase().includes(inputLowerCase) ||
                                option.taskTypeName.toLowerCase().includes(inputLowerCase) ||
                                option.recnum.toString().includes(inputLowerCase)
                            );
                            return filtered;
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3} alignSelf="flex-start">
                        <SMBSelect
                          label="Task Type"
                          variant="outlined"
                          required
                          disabled={
                            isLoadingTaskTypes ||
                            Boolean(
                              sageTaskIdOptions.find(
                                option => option.recnum === Number(values.sageTaskId)
                              )
                            )
                          }
                          error={errors?.taskTypeNumber ? true : false}
                          value={values.taskTypeNumber}
                          onChange={e => {
                            setFieldValue('taskTypeNumber', e.target.value as string);
                          }}
                          handleClear={() => setFieldValue('taskTypeNumber', '')}
                        >
                          {taskTypes.map((taskType: ITaskType, index) => {
                            return (
                              <MenuItem key={`${index}`} value={taskType.taskTypeNumber}>
                                {taskType.taskTypeName}
                              </MenuItem>
                            );
                          })}
                        </SMBSelect>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3} alignSelf="flex-start">
                        <SMBSelect
                          label="Checklist Type"
                          variant="outlined"
                          required
                          disabled={isLoadingTypes}
                          error={errors?.typeId ? true : false}
                          value={values.typeId}
                          onChange={e => {
                            setFieldValue('typeId', e.target.value as string);
                          }}
                          handleClear={() => setFieldValue('typeId', '')}
                        >
                          {types.map((type: IChecklistType, index) => {
                            return (
                              <MenuItem key={`${index}`} value={type.checklistTypeId}>
                                {type.name}
                              </MenuItem>
                            );
                          })}
                        </SMBSelect>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3} alignSelf="flex-start">
                        <SMBSelect
                          label="Predecessor"
                          variant="outlined"
                          disabled={isLoadingTemplates}
                          error={errors?.predecessorChecklistTemplateId ? true : false}
                          value={values.predecessorChecklistTemplateId}
                          onChange={e => {
                            setFieldValue(
                              'predecessorChecklistTemplateId',
                              e.target.value as string
                            );
                          }}
                          handleClear={() => setFieldValue('predecessorChecklistTemplateId', '')}
                        >
                          {checklistTemplates.map((temp: IChecklistTemplate, index) => {
                            return (
                              <MenuItem key={`${index}`} value={temp.checklistTemplateId}>
                                {temp.name}
                              </MenuItem>
                            );
                          })}
                        </SMBSelect>
                      </Grid>
                    </Grid>
                  </Card>
                  <ChecklistTemplatesWorkItems
                    currentWorkItemIndex={currentWorkItemIndex}
                    setCurrentWorkItemIndex={setCurrentWorkItemIndex}
                    setFieldValue={setFieldValue}
                    values={values}
                    setSelectedCatalogImage={setSelectedCatalogImage}
                    catalogs={catalogs}
                    workItemCategories={workItemCategories}
                    requirementTypes={requirementTypes}
                    setModalImageSrc={setModalImageSrc}
                    isLoadingCategories={isLoadingCategories}
                    isLoadingCatalogs={isLoadingCatalogs}
                    handleBlur={handleBlur}
                    hasDuplicateCategory={hasDuplicateCategory}
                    selectedCatalogImage={selectedCatalogImage}
                  />
                  <ChecklistTemplatesMaterialsProvided
                    currentMaterialIndex={currentMaterialIndex}
                    setCurrentMaterialIndex={setCurrentMaterialIndex}
                    setFieldValue={setFieldValue}
                    values={values}
                    handleBlur={handleBlur}
                  />
                  <Box marginTop="2rem">
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={<Save />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        if (!deepEqual(initialValues, values)) {
                          const result = window.confirm(
                            'You have unsaved changes, are you sure you want to exit?'
                          );
                          if (result) {
                            resetForm();
                            handleClose();
                          } else {
                            return;
                          }
                        } else {
                          handleClose();
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
      {!!modalImageSrc && (
        <ImageModal
          isOpen={!!modalImageSrc}
          onClose={() => setModalImageSrc(null)}
          source={modalImageSrc}
        />
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  primaryHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    marginBottom: theme.spacing(1)
  },
  marginBottom: {
    marginBottom: theme.spacing(1)
  },
  cardContent: {
    paddingTop: 0
  },
  paperBorder: {
    border: `1px solid ${theme.palette.grey[300]}`
  },
  radioWrapper: {
    flexDirection: 'row'
  },
  currentFileLink: {
    color: theme.palette.primary.main
  },
  remove: {
    color: theme.palette.error.main,
    fontSize: theme.typography.caption.fontSize,
    margin: 0
  },
  icon: {
    height: '15px',
    marginLeft: theme.spacing(0.5)
  },
  title: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  tradePartnerContacts: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    paddingBottom: 0
  },
  tradePartnerContactsTitle: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(0.5),
    '@media (min-width: 600px)': {
      justifyContent: 'flex-start'
    }
  },
  tradePartnerContactsButton: {
    marginBottom: theme.spacing(1)
  }
}));
